import { AUTH_STAGE_TIMEOUT_MS, runAuthStage } from './authTelemetry';

export interface AuthRetryConfig {
  method?: string;
  url?: string;
  baseURL?: string;
  headers?: any;
  _scribeshadowAuthRetried?: boolean;
  [key: string]: unknown;
}

export const API_SESSION_EXPIRED_EVENT = 'scribeshadow:session-expired';

interface AuthRetryUser {
  uid: string;
  getIdToken: (forceRefresh?: boolean) => Promise<string>;
}

interface ApiAuthErrorHandlerOptions {
  apiBase: string;
  getCurrentUser: () => AuthRetryUser | null;
  replayRequest: (config: AuthRetryConfig) => Promise<unknown>;
  terminateSession: (userId: string) => Promise<void> | void;
  setAuthorizationHeader?: (config: AuthRetryConfig, token: string) => void;
  recoveryState?: ApiAuthRecoveryState;
}

const SAFE_AUTH_REPLAY_METHODS = new Set(['get', 'head', 'options']);

export const isApiRequestUrl = (requestUrl: string, apiBase: string): boolean => {
  if (!requestUrl) return false;
  if (!/^(?:[a-z][a-z0-9+.-]*:|\/\/)/i.test(requestUrl)) return true;
  try {
    const base = new URL(
      apiBase,
      typeof window !== 'undefined' ? window.location.origin : 'http://localhost',
    );
    const request = new URL(requestUrl, base);
    if (request.origin !== base.origin || request.username || request.password) return false;

    const basePath = base.pathname.replace(/\/+$/, '');
    return !basePath
      || request.pathname === basePath
      || request.pathname.startsWith(`${basePath}/`);
  } catch {
    // Never attach credentials to an absolute URL that cannot be matched safely.
    return false;
  }
};

/** Match Axios' baseURL + relative URL combination before attaching credentials. */
export const isApiRequestConfig = (config: { url?: string; baseURL?: string }, apiBase: string): boolean => {
  const url = String(config.url || '');
  const absolute = /^(?:[a-z][a-z0-9+.-]*:|\/\/)/i.test(url);
  const effective = absolute ? url : `${String(config.baseURL || apiBase).replace(/\/+$/, '')}/${url.replace(/^\/+/, '')}`;
  return isApiRequestUrl(effective, apiBase);
};

const isSafeAuthReplay = (config: AuthRetryConfig): boolean =>
  SAFE_AUTH_REPLAY_METHODS.has(String(config.method || 'get').trim().toLowerCase());

export class ApiAuthRecoveryState {
  private refreshPromise: Promise<string> | null = null;

  private refreshUserId: string | null = null;

  private terminalPromise: Promise<void> | null = null;

  private terminalUserId: string | null = null;

  refreshTokenOnce(user: AuthRetryUser): Promise<string> {
    if (!this.refreshPromise || this.refreshUserId !== user.uid) {
      this.refreshUserId = user.uid;
      this.refreshPromise = runAuthStage(
        'token_retrieval',
        () => user.getIdToken(true),
        { timeoutMs: AUTH_STAGE_TIMEOUT_MS.token, successSampleRate: 0.05 },
      ).finally(() => {
        this.refreshPromise = null;
        this.refreshUserId = null;
      });
    }
    return this.refreshPromise;
  }

  resetTerminalForDifferentUser(userId: string): void {
    if (this.terminalUserId && this.terminalUserId !== userId) {
      this.terminalPromise = null;
      this.terminalUserId = null;
    }
  }

  resetTerminal(): void {
    this.terminalPromise = null;
    this.terminalUserId = null;
  }

  async terminateSessionOnce(
    userId: string,
    terminateSession: (userId: string) => Promise<void> | void,
  ): Promise<void> {
    if (!this.terminalPromise || this.terminalUserId !== userId) {
      this.terminalUserId = userId;
      this.terminalPromise = (async () => {
        try {
          await terminateSession(userId);
        } catch {
          // A local sign-out failure must not restart the 401 recovery loop.
        }
      })();
    }
    await this.terminalPromise;
  }
}

export const sharedApiAuthRecoveryState = new ApiAuthRecoveryState();

export const createApiAuthResponseErrorHandler = ({
  apiBase,
  getCurrentUser,
  replayRequest,
  terminateSession,
  recoveryState = sharedApiAuthRecoveryState,
  setAuthorizationHeader = (config, token) => {
    config.headers = {
      ...((config.headers as Record<string, unknown>) || {}),
      Authorization: `Bearer ${token}`,
    };
  },
}: ApiAuthErrorHandlerOptions) => {
  return async (error: any): Promise<unknown> => {
    const config = error?.config as AuthRetryConfig | undefined;
    if (
      error?.response?.status !== 401
      || !config
      || !isApiRequestConfig(config, apiBase)
    ) {
      return Promise.reject(error);
    }

    const currentUser = getCurrentUser();
    if (!currentUser) return Promise.reject(error);

    recoveryState.resetTerminalForDifferentUser(currentUser.uid);

    if (config._scribeshadowAuthRetried) {
      await recoveryState.terminateSessionOnce(currentUser.uid, terminateSession);
      return Promise.reject(error);
    }

    let refreshedToken: string;
    try {
      refreshedToken = await recoveryState.refreshTokenOnce(currentUser);
    } catch (_refreshError) {
      await recoveryState.terminateSessionOnce(currentUser.uid, terminateSession);
      return Promise.reject(error);
    }

    // Refresh authentication for every API method, but replay only safe reads.
    // Mutations remain explicit user actions unless their API contract supplies
    // an idempotency guarantee.
    if (!isSafeAuthReplay(config)) return Promise.reject(error);

    config._scribeshadowAuthRetried = true;
    setAuthorizationHeader(config, refreshedToken);
    try {
      return await replayRequest(config);
    } catch (replayError: any) {
      if (replayError?.response?.status === 401) {
        await recoveryState.terminateSessionOnce(currentUser.uid, terminateSession);
      }
      return Promise.reject(replayError);
    }
  };
};
